/**
 * Vite SPA entry point.
 *
 * Mounts React app at #root. Adds 'olytic' class to body so olytic.css
 * base styles apply.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Link } from 'react-router-dom';

import { App } from './App.tsx';
import { type UiLinkProps, UiLinkProvider } from './components/UiLink.tsx';
// PLAT-217: Foundation first, then Olytic's theme for it, then the legacy
// Workspace sheets last, so legacy markup keeps its exact look (Mothership's
// order). Foundation keeps its rules in cascade layers, which unlayered
// legacy rules beat anyway; its `:root` tokens lose to legacy tokens of the
// same name because legacy loads later.
// `@olytic/foundation/base.css` is not loaded: it restyles bare `body`,
// `h1`-`h6`, every `button` (press scale) and the scrollbars, which would
// visibly change legacy screens. Revisit once app.css's own globals retire.
import '@olytic/foundation/styles.css';
import '@olytic/foundation/themes/olytic.css';
import './styles/olytic.css';
import './styles/app.css';

document.body.classList.add('olytic');

const rootEl = document.getElementById('root');
if (rootEl === null) {
  throw new Error('Missing #root element');
}

/**
 * Adapts this app's router to the shared-primitive link contract.
 *
 * An adapter rather than passing `Link` straight through, because the two are
 * not the same type: react-router types `Link` as returning `ReactNode`, which
 * admits `undefined`, while React 18's JSX requires `ReactElement | null`. The
 * app is the right place to absorb that — it knows which router it has, and a
 * studio on a different react-router major writes its own three lines here
 * rather than the library carrying a peer dependency it cannot satisfy for
 * everyone. See UiLink.tsx.
 */
function RouterLink({ to, className, children }: UiLinkProps) {
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <UiLinkProvider link={RouterLink}>
        <App />
      </UiLinkProvider>
    </BrowserRouter>
  </StrictMode>,
);
